/// <reference types="@angular/localize" />

import {
    APP_INITIALIZER,
    enableProdMode,
    ErrorHandler,
    importProvidersFrom,
    LOCALE_ID,
    Provider,
} from '@angular/core';

import { EMPTY, from, Observable } from 'rxjs';
import { bootstrapApplication } from '@angular/platform-browser';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import { User } from '@sentry/angular';

// MSAL: tu as déjà un APP_INITIALIZER pour initialiser MSAL avant démarrage
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { MAT_EXPANSION_PANEL_DEFAULT_OPTIONS } from '@angular/material/expansion';
import { provideRouter, Router, withDebugTracing, withRouterConfig } from '@angular/router';
import { AuthService } from './app/core/services/auth.service';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalService,
} from '@azure/msal-angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { JwtInterceptor } from './app/core/services/jwt.interceptor';
import { NgxsModule } from '@ngxs/store';
import { AuthState } from './app/core/states/auth.state';
import { UsersState } from './app/features/admin/users/users.state';
import { DocumentsState } from './app/shared/states/documents.state';
import { UsersPaginationState } from './app/features/admin/users/users-pagination.state';
import { OrganizationState } from './app/features/admin/organization/organization.state';
import { ProjectsState } from './app/features/main/project/project.state';
import { TimesheetsState } from './app/features/main/timesheet/timesheet.state';
import { DashboardState } from './app/features/main/dashboard/dashboard.state';
import {
    PublicClientApplication,
    BrowserCacheLocation,
    InteractionType,
} from '@azure/msal-browser';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { appRoutes } from './app/app.route';

if (environment.production) {
    enableProdMode();
}

const DSN_SENTRY = 'https://e495041142db2e395cddaba864106388@sentry.energiesdemain.org/61';
if (DSN_SENTRY.includes('https')) {
    Sentry.init({
        dsn: DSN_SENTRY,
        // ↳ recopie ici *exactement* les options que tu avais dans l'ancien main.ts
        //   (tracesSampleRate, tracePropagationTargets, Replay, etc.)
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
}
// the second parameter 'fr' is optional
registerLocaleData(localeFr, 'fr');
export const AZURE_CLIENT_ID = environment.azureClientId;
const AZURE_TENTANT_ID = environment.azureTenantId;

//MiddleWare qui récupère le token de connexion si existant
export function initializeAppFactory(authService: AuthService): () => Observable<User> {
    return () => {
        let token = localStorage.getItem('Authorization');
        if (token) {
            token = token.slice(7); //Remove 'Bearer '
            return from(authService.registerToken(token));
        } else {
            return EMPTY;
        }
    };
}

export function msalInitializeFactory(msal: MsalService) {
    return () => msal.instance.initialize(); // <-- must be awaited by Angular
}

export const MSAL_BOOTSTRAP_PROVIDER: Provider = {
    provide: APP_INITIALIZER,
    useFactory: msalInitializeFactory,
    deps: [MsalService],
    multi: true,
};

// === Factories MSAL (déjà vues précédemment)
export function MSALInstanceFactory() {
    return new PublicClientApplication({
        auth: {
            clientId: AZURE_CLIENT_ID,
            authority: `https://login.microsoftonline.com/${AZURE_TENTANT_ID}`,
            redirectUri: window.location.origin + '/azure-callback',
            postLogoutRedirectUri: window.location.origin,
            navigateToLoginRequestUrl: false,
        },
        cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: { scopes: ['openid', 'profile', 'email'] },
    };
}

bootstrapApplication(AppComponent, {
    providers: [
        { provide: LOCALE_ID, useValue: 'fr' },
        provideHttpClient(withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },

        { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
        MsalGuard,
        MsalService,
        MsalBroadcastService,
        MSAL_BOOTSTRAP_PROVIDER,

        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [AuthService, Sentry.TraceService],
            multi: true,
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: true,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },

        {
            provide: MAT_EXPANSION_PANEL_DEFAULT_OPTIONS,
            useValue: {
                expandedHeight: '28px',
                collapsedHeight: '28px',
            },
        },
        provideEnvironmentNgxMask(),

        // 4) NGXS (identique à AppModule)
        importProvidersFrom(
            NgxsModule.forRoot([
                AuthState,
                UsersState,
                DocumentsState,
                UsersPaginationState,
                OrganizationState,
                ProjectsState,
                TimesheetsState,
                DashboardState,
            ]),
        ),
        // MSAL: on garde ton bootstrap APP_INITIALIZER pour attendre msal.instance.initialize()
        MSAL_BOOTSTRAP_PROVIDER,
        importProvidersFrom(BrowserAnimationsModule),
        provideRouter(appRoutes),
    ],
}).catch((err) => console.error(err));
